import { FC, useMemo, useState } from "react";

import { Pagination } from "@mantine/core";
import { useAppSelector } from "app/hooks";
import { YearTabs } from "components";
import { usePage } from "hooks/usePage";
import { useDebounce } from "react-use";
import PageHeader from "../../components/layout/page-header";
import PageWrapper from "../../components/layout/page-wrapper";
import TableWithCheckbox from "../../components/TableWithCheckbox/TableWithCheckbox";
import { BrokerTypes } from "../../graphql/__generated__/globalTypes";
import { useOffers } from "../../hooks/useOffers";
import { getColumns } from "./columns";
import Overview from "./components/Overview";
import config from "config";
import useNotification from "hooks/useNotification";
import { useRealtimeConnection } from "hooks/useRealTimeConnection";

type Props = {};

const ClosedOffers: FC<Props> = () => {
  usePage("Kava | Closed Offers");
  const { notify } = useNotification();
  const [year, setYear] = useState<string>(new Date().getFullYear().toString());
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [val, setVal] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const { broker } = useAppSelector((state) => state.offers);

  useDebounce(
    () => {
      setDebouncedValue(val);
    },
    1000,
    [val]
  );

  const selectedBroker = broker || BrokerTypes.Visal;
  const isVisal = selectedBroker === BrokerTypes.Visal;

  const visalResult = useOffers({
    broker: BrokerTypes.Visal,
    perPage,
    offer_status: "CLOSED",
    paginateTo: page,
    searchItem: debouncedValue,
    year: Number(year),
  });

  const kekResult = useOffers({
    broker: BrokerTypes.Kek,
    perPage,
    offer_status: "CLOSED",
    paginateTo: page,
    searchItem: debouncedValue,
    year: Number(year),
  });

  const iriskResult = useOffers({
    broker: BrokerTypes.Irisk,
    perPage,
    offer_status: "CLOSED",
    paginateTo: page,
    searchItem: debouncedValue,
    year: Number(year),
  });

  const {
    offers,
    loading,
    total_offers,
    total_shares,
    error,
    from,
    to,
    refresh,
    last_page,
  } = useMemo(() => {
    if (isVisal) {
      return {
        offers: visalResult.offers,
        loading: visalResult.loading,
        total_offers: visalResult.total_offers,
        total_shares: visalResult.total_shares,
        error: visalResult.error,
        from: visalResult.from,
        to: visalResult.to,
        refresh: () => {
          visalResult.refresh();
        },
        last_page: Math.max(visalResult.last_page),
      };
    } else if (selectedBroker === BrokerTypes.Kek) {
      return kekResult;
    } else if (selectedBroker === BrokerTypes.Irisk) {
      return iriskResult;
    }
    return visalResult;
  }, [
    isVisal,
    selectedBroker,
    visalResult,
    kekResult,
    iriskResult,
  ]);

  const columns = useMemo(
    () => getColumns(broker || BrokerTypes.Visal, refresh),
    [broker, refresh]
  );

  const handleClosedOfferUpdate = async (message: any) => {
    refresh();
    await notify("KAVA NOTIFICATION - CLOSED OFFERS", {
      body: message.data,
    });
  };

  useRealtimeConnection({
    subscriptions: [
      {
        channel: config.realtime.channels.KAVA_NOTIFICATION,
        events: [
          config.realtime.events.BROKER_PUBLISHED_OFFER,
          config.realtime.events.BROKER_UNPUBLISH_OFFER,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
          config.realtime.events.REINSURER_INTEREST,
          config.realtime.events.REINSURER_REMOVE_INTEREST,
          config.realtime.events.BROKER_ACCEPTED_INTEREST,
          config.realtime.events.BROKER_REMOVE_REINSURER_INTEREST,
        ],
      },
      {
        channel: config.realtime.channels.CLOSED_BUSINESS,
        events: [config.realtime.events.CLOSED_BUSINESS],
      },
      {
        channel: config.realtime.channels.KEK_CHANNEL,
        events: [
          config.realtime.events.BROKER_PUBLISHED_OFFER,
          config.realtime.events.BROKER_UNPUBLISH_OFFER,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
          config.realtime.events.REINSURER_INTEREST,
          config.realtime.events.REINSURER_REMOVE_INTEREST,
          config.realtime.events.BROKER_ACCEPTED_INTEREST,
          config.realtime.events.BROKER_REMOVE_REINSURER_INTEREST,
        ],
        pusherInstanceId: "kek",
      },
      {
        channel: config.realtime.channels.SAMPLE_CHANNEL,
        events: [
          config.realtime.events.BROKER_PUBLISHED_OFFER,
          config.realtime.events.BROKER_UNPUBLISH_OFFER,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
          config.realtime.events.REINSURER_INTEREST,
          config.realtime.events.REINSURER_REMOVE_INTEREST,
          config.realtime.events.BROKER_ACCEPTED_INTEREST,
          config.realtime.events.BROKER_REMOVE_REINSURER_INTEREST,
        ],
        pusherInstanceId: "sample",
      },
    ],
    callback: handleClosedOfferUpdate,
  });

  return (
    <PageWrapper>
      <PageHeader title="Facultative Offers" />
      <Overview
        stats={[
          {
            name: "Total Offers",
            value: total_offers ?? 0,
          },
          ...(total_shares
            ? Object.keys(total_shares).map((e) => ({
              name: `Total ${e}`,
              value: `${total_shares[e]}`,
              description: "Premium",
            }))
            : []),
        ]}
      />
      <YearTabs
        year={year}
        setYear={setYear}
        setActiveTab={(value: BrokerTypes) => {
          setPage(1);
          setVal("");
        }}
        activeTab={broker || BrokerTypes.Visal}
      />
      <section className="mt-8" aria-labelledby="gallery-heading">
        <TableWithCheckbox
          columns={columns}
          data={offers}
          searchValue={val}
          search={(value: string) => setVal(value)}
          setSelected={() => { }}
          selected={[]}
          loading={loading}
          error={error}
          refresh={refresh}
          setNumberOfRows={setPerPage}
          numberOfRows={perPage}
        />
      </section>
      <section className="p-3 flex justify-between items-center">
        <span>
          Showing {from} to {to} of {total_offers} items
        </span>
        <Pagination
          total={last_page}
          page={page}
          onChange={(page) => {
            setPage(page);
          }}
        />
      </section>
    </PageWrapper>
  );
};

export default ClosedOffers;
